import { CSV_BULK_ORDER_ROW_HEADERS, CSV_CARGO_ORDER_ROW_HEADERS } from '#features/csvImport/CsvOrderRow.ts';
import type { ImportOrderFormData } from '#features/csvImport/ImportOrderFormValidation.ts';
import { mapBulkRawRow } from '#features/csvImport/parsing/bulkRawRowMapper.ts';
import { mapCargoRawRow } from '#features/csvImport/parsing/cargoRawRowMapper.ts';
import { parseBulkOrderRow, parseCargoOrderRow } from '#features/csvImport/parsing/csvOrderRowParser.ts';

import { CSV_DELIMITER } from './constants.ts';

const sanitizeContent = (content: string) => content.replace(/^\uFEFF/, '');

const getNonEmptyLines = (content: string) =>
    content
        .split(/\r?\n/)
        .map(line => line.trimEnd())
        .filter(line => line.trim().length > 0);

const isEmptyCsvRow = (line: string) => line.split(CSV_DELIMITER).every(column => column.trim().length === 0);

const getDataLines = (lines: string[]) => lines.slice(2).filter(line => !isEmptyCsvRow(line));

const getHeaderErrors = (headerColumns: string[], expectedHeaders: string[]) =>
    expectedHeaders
        .map((expected, index) => {
            const actual = headerColumns[index]?.replace(/^\uFEFF/, '').trim() ?? '';
            if (actual !== expected) {
                return `Header mismatch at column ${index + 1}: expected "${expected}" but received "${actual}".`;
            }
            return undefined;
        })
        .filter((error): error is string => Boolean(error));

export const parseCsvImport = async (content: string): Promise<ImportOrderFormData> => {
    const sanitized = sanitizeContent(content);
    const lines = getNonEmptyLines(sanitized);

    if (lines.length <= 2) {
        throw new Error('CSV file must include a header and at least one data row.');
    }

    const dataLines = getDataLines(lines);
    if (dataLines.length === 0) {
        throw new Error('CSV file must include a header and at least one data row.');
    }

    const headerColumns = lines[1].split(CSV_DELIMITER);
    if (headerColumns[26] === CSV_BULK_ORDER_ROW_HEADERS[26]) {
        return parseBulkCsvImport(dataLines, headerColumns);
    }
    if (headerColumns[26] === CSV_CARGO_ORDER_ROW_HEADERS[26]) {
        return parseCargoCsvImport(dataLines, headerColumns);
    }

    throw new Error('Invalid CSV file');
};

const parseBulkCsvImport = async (dataLines: string[], headerColumns: string[]): Promise<ImportOrderFormData> => {
    const headerErrors = getHeaderErrors(headerColumns, CSV_BULK_ORDER_ROW_HEADERS);
    if (headerErrors.length > 0) {
        throw new Error(`Invalid CSV header: ${headerErrors.join(' ')}`);
    }

    const orders = await Promise.all(
        dataLines.map(async line => {
            const columns = line.split(CSV_DELIMITER);
            const rawRow = mapBulkRawRow(columns);
            return parseBulkOrderRow(rawRow);
        })
    );

    return { orders };
};

const parseCargoCsvImport = async (dataLines: string[], headerColumns: string[]): Promise<ImportOrderFormData> => {
    const headerErrors = getHeaderErrors(headerColumns, CSV_CARGO_ORDER_ROW_HEADERS);
    if (headerErrors.length > 0) {
        throw new Error(`Invalid CSV header: ${headerErrors.join(' ')}`);
    }

    const orders = await Promise.all(
        dataLines.map(async line => {
            const columns = line.split(CSV_DELIMITER);
            const rawRow = mapCargoRawRow(columns);
            return parseCargoOrderRow(rawRow);
        })
    );

    return { orders };
};
